import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, UserPlus, Building, Sparkles, CheckCircle, CreditCard, Calendar, Star, Users, Shield, Zap, Phone } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Password validation for customers
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("At least 8 characters long");
    }
    
    if (!/(?=.*[0-9])/.test(password)) {
      errors.push("At least one number");
    }
    
    if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(password)) {
      errors.push("At least one special character");
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push("At least one lowercase letter");
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push("At least one uppercase letter");
    }
    
    return errors;
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Validate password as user types (for customers only)
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate password for customers
    const passwordValidationErrors = validatePassword(formData.password);
    if (passwordValidationErrors.length > 0) {
      toast({
        title: "Password requirements not met",
        description: "Please create a stronger password that meets all requirements.",
        variant: "destructive",
      });
      setPasswordErrors(passwordValidationErrors);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      // Split the full name into first and last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const result = await register({
        email: formData.email,
        password: formData.password,
        first_name: firstName,
        last_name: lastName,
        company_name: formData.company,
        phone_number: formData.phone,
        role: 'customer'
      });
      
      if (result.success) {
        toast({
          title: "Registration successful!",
          description: "Please check your email for a verification code to complete your account setup.",
        });
        // Redirect to email verification page with email parameter
        navigate(`/email-verification?email=${encodeURIComponent(formData.email)}`);
      } else {
        toast({
          title: "Registration failed",
          description: result.error || "Registration failed",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Registration failed",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: CheckCircle,
      title: "Easy Setup Process",
      description: "Get your AI chatbot running in just 3-5 business days"
    },
    {
      icon: Users,
      title: "Expert Support",
      description: "Dedicated team to help you succeed"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Your data is protected with bank-grade encryption"
    },
    {
      icon: Zap,
      title: "Instant Results",
      description: "See immediate improvements in customer satisfaction"
    }
  ];

  const pricingSteps = [
    {
      icon: CreditCard,
      title: "Pay $200 Down Payment",
      description: "Start your setup process",
      highlight: true
    },
    {
      icon: Star,
      title: "We Build Your Chatbot",
      description: "3-5 business days setup",
      highlight: false
    },
    {
      icon: Calendar,
      title: "Pay Remaining $400",
      description: "After successful delivery",
      highlight: false
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-950 overflow-hidden">
      <SEOHead 
        title="Register - Create Your BrixBot AI Account" 
        description="Create your BrixBot account and start transforming your customer service with AI chatbots. Easy setup, expert support, and enterprise security."
        isIndexable={true}
      />
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-violet-500/10 rounded-full blur-2xl animate-pulse delay-2000"></div>
        <div className="absolute bottom-1/4 right-1/4 w-36 h-36 bg-purple-500/10 rounded-full blur-2xl animate-pulse delay-3000"></div>
      </div>

      <div className="relative z-10 py-8 min-h-screen p-4">
        <div className="max-w-7xl w-full mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Sparkles className="w-4 h-4 mr-2" />
              Welcome
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Create Your
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
                BrixBot Account
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Start your AI chatbot journey today. Join thousands of businesses already transforming their customer service.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Registration Form */}
            <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500"></div>
              
              <CardHeader className="text-center relative z-10">
                <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <UserPlus className="w-10 h-10 text-white" />
                </div>
                <CardTitle className="text-3xl text-white">Create Account</CardTitle>
                <CardDescription className="text-gray-300 text-lg">
                  Start with $200 down payment, then $400 after setup
                </CardDescription>
              </CardHeader>
              
              <CardContent className="relative z-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-300 font-medium">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 h-12 rounded-xl"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300 font-medium">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 h-12 rounded-xl"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-300 font-medium">Company Name</Label>
                    <div className="relative">
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Enter your company name"
                        className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 pl-12 h-12 rounded-xl"
                        required
                      />
                      <Building className="absolute left-4 top-3 w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-300 font-medium">Phone Number</Label>
                    <div className="relative">
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your phone number with country code (e.g., +1234567890)"
                        className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 pl-12 h-12 rounded-xl"
                        required
                      />
                      <Phone className="absolute left-4 top-3 w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Please include your country code (e.g., +1 for US, +44 for UK)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-300 font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Create a password"
                        className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 h-12 rounded-xl pr-12"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1 h-10 w-10 text-gray-400 hover:text-gray-300"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    
                    {/* Password Requirements */}
                    {formData.password && (
                      <div className="mt-2 p-3 bg-gray-800/30 rounded-lg border border-gray-700">
                        <p className="text-xs text-gray-400 mb-2">Password requirements:</p>
                        <div className="space-y-1">
                          {[
                            { req: "At least 8 characters long", valid: formData.password.length >= 8 },
                            { req: "At least one number", valid: /(?=.*[0-9])/.test(formData.password) },
                            { req: "At least one special character", valid: /(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(formData.password) },
                            { req: "At least one lowercase letter", valid: /(?=.*[a-z])/.test(formData.password) },
                            { req: "At least one uppercase letter", valid: /(?=.*[A-Z])/.test(formData.password) }
                          ].map((item, index) => (
                            <div key={index} className={`flex items-center space-x-2 text-xs ${item.valid ? 'text-green-400' : 'text-gray-400'}`}>
                              <CheckCircle className={`w-3 h-3 ${item.valid ? 'text-green-400' : 'text-gray-500'}`} />
                              <span>{item.req}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-gray-300 font-medium">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm your password"
                        className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 h-12 rounded-xl pr-12"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1 h-10 w-10 text-gray-400 hover:text-gray-300"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="terms"
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-violet-600 focus:ring-violet-500"
                      required
                    />
                    <Label htmlFor="terms" className="text-gray-300 text-sm">
                      I agree to the{' '}
                      <Link to="/terms" className="text-violet-400 hover:text-violet-300 transition-colors">
                        Terms of Service
                      </Link>
                      {' '}and{' '}
                      <Link to="/privacy" className="text-violet-400 hover:text-violet-300 transition-colors">
                        Privacy Policy
                      </Link>
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-3" />
                        Create Account
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-8 text-center">
                  <p className="text-gray-400 text-lg">
                    Already have an account?{' '}
                    <Link to="/login" className="text-violet-400 hover:text-violet-300 transition-colors font-medium">
                      Sign in
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Features & Process */}
            <div className="space-y-8">
              {/* What happens next */}
              <Card className="bg-gradient-to-r from-violet-900/30 to-purple-900/30 border-violet-500/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl text-white flex items-center">
                    <Star className="w-6 h-6 mr-3 text-violet-400" />
                    What happens next?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {pricingSteps.map((step, index) => (
                      <div key={index} className={`flex items-start space-x-4 p-4 rounded-xl ${step.highlight ? 'bg-violet-500/10 border border-violet-500/30' : 'bg-gray-800/30'}`}>
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${step.highlight ? 'bg-violet-500/20' : 'bg-gray-700'}`}>
                          <step.icon className={`w-6 h-6 ${step.highlight ? 'text-violet-400' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{step.title}</h4>
                          <p className="text-gray-400 text-sm">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <Card key={index} className="bg-gray-900/30 border-violet-500/20 backdrop-blur-sm hover:border-violet-400/40 transition-all duration-300 group">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <feature.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                          <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pricing Summary */}
              <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-white">Pricing Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Setup Fee (Total)</span>
                      <span className="text-white font-semibold">$600</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Down Payment</span>
                      <span className="text-violet-400 font-semibold">$200</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Final Payment</span>
                      <span className="text-gray-400">$400</span>
                    </div>
                    <div className="border-t border-gray-700 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Monthly Hosting</span>
                        <span className="text-white font-semibold">Choose your plan</span>
                      </div>
                      <div className="text-sm text-gray-400 mt-1">
                        Starting from $25/month (see Pricing page)
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;